/**
 * Import third-party libraries.
 */
import styled from 'styled-components';

/**
 * Import custom style properties.
 */
import {COLORS, focusStyles, scrollBarStyling} from '../styles';
import {
  ComponentLStyling,
  ComponentMStyling,
  ComponentSStyling,
  ComponentTextStyle,
  ComponentXSStyling
} from '../styles/typography';
import {Z_INDEXES} from '../styles/z-indexes';
import {StyledCheckBox} from '../InputFields/Checkbox';
import {Size} from "../types";

/**
 * Table styles
 */

export const TableWrapper = styled.div`
  position: relative;

  &.border{
    border-left: 1px solid ${COLORS.neutral_200};
    border-right: 1px solid ${COLORS.neutral_200};
  }
`;


export const StyledTable = styled.table`
  background: transparent;
  border-collapse: collapse;
  width: 100%;

  &.fixed {
    table-layout: fixed;
  }

  &.auto {
    table-layout: auto;
  }
`;

export const StyledTableHeader = styled.div`
`;

export const StyledTableHeaderTitle = styled.div`
`;

export const StyledTableHeaderTitleContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  box-sizing: border-box;
  min-height: 56px;
  border-bottom: 1px solid ${COLORS.neutral_200};
  border-top: 2px solid ${COLORS.neutral_200};
  padding: 0 16px;

  ${ComponentLStyling(ComponentTextStyle.Bold, COLORS.black)}
  .title-menu {
    margin-right: -16px;
  }
`;

export const StyledTableHeaderRow = styled.thead`
`;

export const StyledTableHeaderColumns = styled.tr`
`;

export const StyledTableHeaderColumnContent = styled.div`
  box-sizing: border-box;
  min-height: 56px;
  padding: 0 16px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${COLORS.neutral_200};
  border-bottom: 1px solid ${COLORS.neutral_200};
`;

export const StyledTableHeaderColumnSortDirection = styled.span`
  width: 24px;
  height: 24px;
`;

export const StyledTableHeaderColumn = styled.th`
  ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  position: relative;
  text-align: unset;
  background-color: ${COLORS.white};

  //fixes header heights no idea why
  height: 1px;

  &.sortable {
    cursor: pointer;

    &:hover {
      background-color: ${COLORS.primary_20};
      color: ${COLORS.primary_700};
      z-index: ${Z_INDEXES.hover};
    }

    &:active {
      background-color: ${COLORS.primary_100};
      color: ${COLORS.primary_800};
      z-index: ${Z_INDEXES.active};
    }

    &:focus {
      ${focusStyles}
    }
  }

  &.left {
    & > div {
      justify-content: left;
    }
  }

  &.right {
    & > div {
      justify-content: right;
    }
  }

  &.center {
    & > div {
      justify-content: center;
    }
  }

  &.sortable ${StyledTableHeaderColumnContent} > svg {
    opacity: 0;
  }

  &.sortable.sorted ${StyledTableHeaderColumnContent} > svg {
    opacity: 1;
  }

  &.sorted {
    background-color: ${COLORS.neutral_20};
  }
`;

export const StyledTableBodyRow = styled.tr`
  cursor: pointer;
  position: relative;
  outline: none;
  background-color: ${COLORS.white};
  
  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS.neutral_200};
  }

  &.selected{
    background-color: ${COLORS.primary_100};
  }
  
  &:hover {
    background-color: ${COLORS.primary_100};
    z-index: ${Z_INDEXES.hover};
  }

  &:focus {
    ${focusStyles}
  }

  &:active {
    z-index: ${Z_INDEXES.active};
  }

`;

export const StyledTableNoRowsLabelRow = styled.tr`
  position: relative;
  outline: none;
  background-color: ${COLORS.neutral_50};
`;

export const StyledTableBody = styled.tbody`
`;


export const StyledTableCell = styled.td``;

export const StyledTableCellContent = styled.div<{withNote?: boolean}>`
  min-height: ${props => props.withNote ? '64px' : '48px'};

  padding: 0 16px;
  ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.black)}

  display: flex;
  align-items: center;
  justify-content: left;
  gap: 8px;

  &.truncate-text,
  &.truncate-text span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.left {
    justify-content: left;
  }

  &.right {
    justify-content: right;
  }

  &.center {
    justify-content: center;
  }

  a:first-letter,
  span:first-letter {
    text-transform: uppercase;
  }
  
  ${StyledCheckBox}{
    width: fit-content;
  }
`;

export const StyledTableCellIcon = styled.div`
  color: ${COLORS.neutral_700};
  width: 20px;
  height: 20px;

  svg {
    color: ${COLORS.neutral_700};
    width: 20px;
    height: 20px;
  }
`;

export const StyledTableCellText = styled.span`
  display: block;
`;

export const StyledTableCellTextWrapper = styled.div<{ textWrap?: boolean }>`
  padding: 12px 0px;
  width: 100%;
  ${props => !props.textWrap ? `
  overflow: hidden;
  text-overflow: ellipsis;
  ` : ``}
`;

export const StyledTableCellNoteText = styled.span`
  ${ComponentXSStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
  display: inline-block;
  width: 100%;
`;


export const StyledTableFooter = styled.div`
`;

export const StyledTableFooterContent = styled.div`
  border-top: 2px solid ${COLORS.neutral_200};
  border-bottom: 1px solid ${COLORS.neutral_200};
  box-sizing: border-box;
  min-height: 56px;

  display: flex;
  align-items: center;
  justify-content: right;

`;

export const StyledTableFooterCurrentInfo = styled.span`
  padding: 0 16px;
  ${ComponentXSStyling(ComponentTextStyle.Regular, COLORS.black)}
`;

export const StyledTableFooterControls = styled.div`
  display: flex;
`;

export const StyledTableSpinner = styled.div<{zindex?: string}>`
  height: 100%;
  width: 100%;
  position: absolute;
  background-color: rgba(255, 255, 255, 0.75);
  top: 0;
  z-index: ${(props) => (props.zindex ?? Z_INDEXES.loader)};
`;

export const StyledTableFooterCollapseButton = styled.button`
  position: relative;
  border-bottom: 1px solid ${COLORS.neutral_200};
  border-top: 1px solid ${COLORS.neutral_200};
  border-left: none;
  border-right: none;
  box-sizing: border-box;
  min-height: 48px;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: ${COLORS.white};

  cursor: pointer;

  ${ComponentMStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  
  &:focus {
    ${focusStyles}
  }

  &:hover {
    z-index: ${Z_INDEXES.hover};
    background: ${COLORS.primary_20};
    color: ${COLORS.primary_700};
  }

  &:active {
    z-index: ${Z_INDEXES.active};
    background: ${COLORS.primary_100};
    color: ${COLORS.primary_800};
  }

  &:disabled {
    display: none;
  }

`;


export const StyledTableBodyWrapper = styled.div`
  overflow-x: auto;
  ${scrollBarStyling(Size.Small)}

  ::-webkit-scrollbar-track {
    margin: 6px;
  }

  &.scrollable{
    margin-bottom: 6px;
    padding-bottom: 10px;
  }
`;
